/**
 * Copyright 2016 Taito United Oy
 * All rights reserved.
 */

import ReactTooltip from 'react-tooltip';
import PropTypes from 'prop-types';
import React  from 'react';
import MdPerson from 'react-icons/lib/md/person'
import { FormattedHTMLMessage } from 'react-intl';
import styled from 'styled-components';

// Messages
import messages from './messages/ReportQualifications_messages';

// Components
import Box, { Minifiable } from '../../components/Box';

function localise(item, params, locale) {
  const localeFallback = ['En', 'Fi', 'Sv'];
  return params.reduce((acc, param) => {
    let value = item[`${param}${locale}`];
    if (!value) {
      const foundLocale = localeFallback.find(l => item[`${param}${l}`]);
      if (foundLocale) {
        value = item[`${param}${foundLocale}`];
      }
    }
    acc[param] = value;
    return acc;
  }, {});
};

const propTypes = {
  locale: PropTypes.object.isRequired,
  report: PropTypes.object.isRequired,
};

const ReportQualifications = ({ locale, report }) => {
  // Don't render if there's nothing to display
  if (!report.competence.length && !report.qualifications.length) return null;

  const capitalizedLocale = `${locale.lang[0].toUpperCase()}${locale.lang[1]}`;

  const renderStamp = (type, qualification) => {
    const localised = localise(
      qualification,
      ['description', 'logo', 'name', 'url'],
      capitalizedLocale,
    );
    let linkProps = { as: 'span' };
    if (localised.url && type !== 'competence') {
      linkProps = {
        as: 'a',
        href: localised.url,
        rel: 'noreferrer noopenner',
        target: '_blank',
      }
    }
    return (
      <Qualification
        id={`company-${type}-${qualification.slug}`}
        key={`company-${type}-${qualification.slug}`}
        data-tip={localised.description || ''}
        data-for='company-competence-tooltip'
        {...linkProps}
      >
        {localised.logo && <Logo role="presentation" src={localised.logo} />}
        <Name>{localised.name}</Name>
        {qualification.valid && (
          <Amount>
            <AmountIcon />
            <Valid>{qualification.valid}</Valid>
            <FormattedHTMLMessage {...messages.competencePerson} />
          </Amount>
        )}
      </Qualification>
    );
  }

  const renderQualification = qualification =>
    renderStamp('qualification', qualification);

  const renderCompetence = competence =>
    renderStamp('competence', competence);

  return (
    <Box
      headerLabel={<FormattedHTMLMessage {...messages.title} />}
    >
      {report.qualifications.length > 0 && (
        <React.Fragment>
          <Title>
            <FormattedHTMLMessage {...messages.qualificationsTitle} />
          </Title>
          <CompetenceList>
            {report.qualifications.map(renderQualification)}
          </CompetenceList>
          <Minifiable>
            <CompetenceInfo>
              <FormattedHTMLMessage {...messages.qualificationInfo} />
            </CompetenceInfo>
          </Minifiable>
        </React.Fragment>
      )}
      {report.competence.length > 0 && (
        <React.Fragment>
          <Title><FormattedHTMLMessage {...messages.competenceTitle} /></Title>
          <CompetenceList>
            {report.competence.map(renderCompetence)}
          </CompetenceList>
          <Minifiable>
            <CompetenceInfo>
              <FormattedHTMLMessage {...messages.competenceInfo} />
            </CompetenceInfo>
          </Minifiable>
        </React.Fragment>
      )}
      <Tooltip
        id='company-competence-tooltip'
        overridePosition={({ left, top }, _e, _t, node) => ({
          top: typeof node === 'string' ? top : Math.max(top, 0),
          left: typeof node === 'string' ? left : Math.max(left, 0),
        })}
      />
    </Box>
  );
};

ReportQualifications.propTypes = propTypes;

const Tooltip = styled(ReactTooltip)`
  max-width: 70vw;
`;

const CompetenceList = styled.div`
  align-items: center;
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  margin: -6px;
  margin-bottom: 12px;

  &:last-child {
    margin-bottom -6px;
  }
`;

const Title = styled.strong`
  line-height: 2rem;
`;

const Qualification = styled.div`
  align-items: center;
  border: 1px solid ${props => props.theme['grey-normal']};
  border-radius: 6px;
  display: flex;
  flex-direction: row;
  margin: 6px;
  padding: 6px;
`;

const Logo = styled.img`
  margin-right: 6px;
  max-width: 100%;
  max-height: 1rem;
`;

const Name = styled.span`
  white-space: nowrap;
`;

const Amount = styled.div`
  align-items: center;
  display: flex;
  padding-left: 12px;
`;

const AmountIcon = styled(MdPerson)`
  font-size: 1.2rem;
`;

const Valid = styled.span`
  padding: 0 3px;
`;

const CompetenceInfo = styled.div`
  background-color: ${props => props.theme.beige};
  border-radius: 6px;
  font-size: 10pt;
  line-height: 13pt;
  margin: 0px;
  padding: 12px;

  p {
    margin: 0;

    & + p {
      margin-top: 12px;
    }
  }
`;

export default ReportQualifications;
